import type { ReactNode } from 'react';

interface SkeletonProps {
  className?: string;
}

/**
 * Componente base de Skeleton con animación pulse
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

/**
 * Skeleton para texto de una línea
 */
export function SkeletonText({
  className = '',
  width = 'w-full',
}: SkeletonProps & { width?: string }) {
  return <Skeleton className={`h-4 ${width} ${className}`} />;
}

/**
 * Skeleton para títulos/headings
 */
export function SkeletonTitle({
  className = '',
  width = 'w-48',
}: SkeletonProps & { width?: string }) {
  return <Skeleton className={`h-8 ${width} ${className}`} />;
}

/**
 * Skeleton para avatar/imagen circular
 */
export function SkeletonAvatar({
  size = 'md',
  className = '',
}: SkeletonProps & { size?: 'sm' | 'md' | 'lg' }) {
  const sizes = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  };
  return <Skeleton className={`rounded-full ${sizes[size]} ${className}`} />;
}

/**
 * Skeleton para botones
 */
export function SkeletonButton({
  className = '',
  width = 'w-24',
}: SkeletonProps & { width?: string }) {
  return <Skeleton className={`h-10 ${width} rounded-lg ${className}`} />;
}

/**
 * Skeleton para badges/chips
 */
export function SkeletonBadge({ className = '' }: SkeletonProps) {
  return <Skeleton className={`h-6 w-20 rounded-full ${className}`} />;
}

/**
 * Skeleton para imágenes
 */
export function SkeletonImage({
  className = '',
  aspectRatio = 'aspect-video',
}: SkeletonProps & { aspectRatio?: string }) {
  return <Skeleton className={`w-full ${aspectRatio} rounded-lg ${className}`} />;
}

/**
 * Skeleton para tarjetas de estadísticas (StatCard)
 */
export function SkeletonStatCard({ className = '' }: SkeletonProps) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-32 mb-2" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

/**
 * Skeleton para tarjeta completa
 */
export function SkeletonCard({
  className = '',
  showImage = false,
  lines = 3,
}: SkeletonProps & { showImage?: boolean; lines?: number }) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      {showImage && <SkeletonImage />}
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-3/4" />
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} className={`h-4 ${i === lines - 1 ? 'w-1/2' : 'w-full'}`} />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton para una fila de tabla
 */
export function SkeletonTableRow({
  columns = 5,
  className = '',
}: SkeletonProps & { columns?: number }) {
  return (
    <tr className={className}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-6 py-4 whitespace-nowrap">
          <Skeleton className={`h-4 ${i === 0 ? 'w-32' : 'w-20'}`} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton para tabla completa
 */
export function SkeletonTable({
  rows = 5,
  columns = 5,
  className = '',
}: SkeletonProps & { rows?: number; columns?: number }) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <Skeleton className="h-3 w-20" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Skeleton para evento (card de evento)
 */
export function SkeletonEventCard({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}
    >
      {/* Imagen */}
      <Skeleton className="w-full h-36 sm:h-48" />

      {/* Contenido */}
      <div className="p-4 space-y-3">
        {/* Título y badge */}
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-6 w-3/5" />
          <SkeletonBadge />
        </div>

        {/* Info del evento */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>

        {/* Descripción */}
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />

        {/* Botones */}
        <div className="pt-4 border-t border-gray-100 flex gap-2">
          <Skeleton className="h-9 flex-1 rounded-lg" />
          <Skeleton className="h-9 w-10 rounded-lg" />
          <Skeleton className="h-9 w-10 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton para lista de usuarios
 */
export function SkeletonUserRow({ className = '' }: SkeletonProps) {
  return (
    <tr className={className}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <SkeletonAvatar size="sm" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <SkeletonBadge />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Skeleton className="h-4 w-28" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Skeleton className="h-4 w-24" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </td>
    </tr>
  );
}

/**
 * Skeleton para Dashboard Home
 */
export function SkeletonDashboardHome() {
  return (
    <div className="space-y-6">
      {/* Título */}
      <SkeletonTitle width="w-40" />

      {/* Grid de cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Card Pagos Pendientes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <Skeleton className="h-5 w-32 mb-4 bg-yellow-200" />
          <Skeleton className="h-12 w-16 mb-4 bg-yellow-200" />
          <Skeleton className="h-9 w-32 rounded-lg bg-yellow-200" />
        </div>

        {/* Card Próximos Eventos */}
        <div className="sm:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <Skeleton className="h-5 w-36 mb-4" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex justify-between items-center pb-3 border-b last:border-0"
              >
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <Skeleton className="h-9 w-24 rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        {/* Card Compras Recientes */}
        <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <Skeleton className="h-5 w-36 mb-4" />
          <SkeletonTable rows={5} columns={5} />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton para lista de eventos
 */
export function SkeletonEventsList({ count = 6 }: { count?: number }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <SkeletonTitle width="w-40" />
        <Skeleton className="h-10 w-36 rounded-lg" />
      </div>

      {/* Grid de eventos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonEventCard key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton para página de pagos pendientes
 */
export function SkeletonPaymentsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <SkeletonTitle width="w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32 rounded-lg" />
          <Skeleton className="h-10 w-24 rounded-lg" />
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-9 w-28 rounded-lg" />
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SkeletonTable rows={10} columns={7} />
      </div>
    </div>
  );
}

/**
 * Skeleton para página de asistentes
 */
export function SkeletonAttendeesPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <SkeletonTitle width="w-36" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-12 w-40 rounded-lg" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      {/* Filtros y tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-3 flex gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-28 rounded-lg" />
          ))}
        </div>
        <div className="p-6">
          <SkeletonTable rows={8} columns={5} />
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton para página de usuarios
 */
export function SkeletonUsersPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <SkeletonTitle width="w-56" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-36 rounded-lg" />
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-24 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3">
                <Skeleton className="h-3 w-16" />
              </th>
              <th className="px-6 py-3">
                <Skeleton className="h-3 w-12" />
              </th>
              <th className="px-6 py-3">
                <Skeleton className="h-3 w-20" />
              </th>
              <th className="px-6 py-3">
                <Skeleton className="h-3 w-16" />
              </th>
              <th className="px-6 py-3">
                <Skeleton className="h-3 w-20" />
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: 10 }).map((_, i) => (
              <SkeletonUserRow key={i} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Skeleton para página de reportes
 */
export function SkeletonReportsPage() {
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <Skeleton className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-7 sm:h-8 w-48 sm:w-56" />
          <Skeleton className="h-4 w-36" />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex -mb-px">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 py-3 sm:py-4 px-2 sm:px-6 flex justify-center">
                <Skeleton className="h-5 w-24 sm:w-32" />
              </div>
            ))}
          </div>
        </div>

        {/* Contenido de tab */}
        <div className="p-3 sm:p-6 space-y-6">
          {/* Filtros */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
              ))}
            </div>
          </div>

          {/* Columnas */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-32" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <Skeleton key={i} className="h-10 rounded-lg" />
              ))}
            </div>
          </div>

          {/* Stats de preview */}
          <div className="flex flex-wrap gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-32 rounded-lg" />
            ))}
          </div>

          {/* Tabla preview */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-64">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <th key={i} className="px-3 py-2">
                        <Skeleton className="h-4 w-16" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {[1, 2, 3, 4, 5].map((row) => (
                    <tr key={row}>
                      {[1, 2, 3, 4, 5, 6].map((col) => (
                        <td key={col} className="px-3 py-2">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Exportar */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
            <Skeleton className="h-5 w-36" />
            <div className="flex gap-2">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-4 w-20" />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Contenedor con skeleton loading genérico
 */
interface SkeletonWrapperProps {
  isLoading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}

export function SkeletonWrapper({ isLoading, skeleton, children }: SkeletonWrapperProps) {
  if (isLoading) {
    return <>{skeleton}</>;
  }
  return <>{children}</>;
}

export default Skeleton;
